import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Download, Sparkles, Trash2, X } from "lucide-react";
import { AuthImage, fileUrl } from "@/components/AuthImage";
import { catOf, UNSORTED } from "./usePhotoCategories";

function CaptionEditor({ photo, canEdit, onSave }) {
  const [caption, setCaption] = useState(photo.caption || "");
  useEffect(() => setCaption(photo.caption || ""), [photo.id, photo.caption]);
  const dirty = caption.trim() !== (photo.caption || "");
  if (!canEdit) return photo.caption ? <p className="text-sm text-white" data-testid="viewer-caption">{photo.caption}</p> : null;
  return (
    <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); onSave({ caption }); }}>
      <input value={caption} onChange={(e) => setCaption(e.target.value)} maxLength={500} placeholder="Add a caption…" data-testid="viewer-caption-input"
        onKeyDown={(e) => e.stopPropagation()}
        className="flex-1 min-w-0 h-10 rounded-full bg-white/10 border border-white/15 px-4 text-sm text-white placeholder:text-white/50 focus:outline-none focus:border-[#FF5F15]" />
      {dirty && <button type="submit" className="h-10 px-4 rounded-full bg-[#FF5F15] text-white text-sm font-medium hover:bg-[#E65100]" data-testid="viewer-caption-save">Save</button>}
    </form>
  );
}

export function PhotoViewer({ photos, categories, index, onIndex, onClose, canEdit, onSave, onDelete }) {
  const [touchX, setTouchX] = useState(null);
  const open = index !== null && photos[index];
  const n = photos.length;
  const go = (d) => onIndex((index + d + n) % n);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft" && n > 1) go(-1);
      if (e.key === "ArrowRight" && n > 1) go(1);
    };
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    [index - 1, index + 1].forEach((i) => { const p = photos[(i + n) % n]; if (p) new Image().src = fileUrl(p.storage_path, { w: 1920 }); });
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = prev; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, index, n]);

  if (!open) return null;
  const p = photos[index];
  const editable = canEdit(p);
  const nav = "w-11 h-11 rounded-full bg-white/10 hover:bg-[#FF5F15] text-white flex items-center justify-center transition-colors";

  return (
    <div className="fixed inset-0 z-[130] bg-zinc-950/[0.97] backdrop-blur-sm flex flex-col animate-in fade-in duration-200" role="dialog" aria-label="Photo viewer" data-testid="photo-viewer">
      <header className="flex items-center gap-2 px-4 pt-[max(0.75rem,env(safe-area-inset-top))] pb-2 text-white">
        <span className="text-sm font-mono text-white/70" data-testid="viewer-counter">{index + 1} / {n}</span>
        <span className="text-sm text-white/60 truncate flex-1">{p.uploaded_by_name} · {p.taken_at}</span>
        <a href={fileUrl(p.storage_path, { download: true })} className={nav} aria-label="Download photo" data-testid="viewer-download"><Download className="w-5 h-5" /></a>
        {editable && <button type="button" onClick={() => onDelete(p)} className={`${nav} hover:!bg-red-600`} aria-label="Delete photo" data-testid="viewer-delete"><Trash2 className="w-5 h-5" /></button>}
        <button type="button" onClick={onClose} className={nav} aria-label="Close viewer" data-testid="viewer-close"><X className="w-5 h-5" /></button>
      </header>
      <div className="relative flex-1 min-h-0 flex items-center justify-center px-2"
        onTouchStart={(e) => setTouchX(e.touches[0]?.clientX ?? null)}
        onTouchEnd={(e) => { if (touchX == null || n < 2) return; const dx = (e.changedTouches[0]?.clientX ?? touchX) - touchX; if (dx > 45) go(-1); else if (dx < -45) go(1); setTouchX(null); }}>
        <div key={p.id} className="relative w-full h-full">
          <AuthImage path={p.storage_path} width={320} plain eager alt="" className="absolute inset-0 w-full h-full [&>img]:object-contain blur-md" />
          <AuthImage path={p.storage_path} width={1920} plain eager alt={p.caption || "Site photo"} className="absolute inset-0 w-full h-full [&>img]:object-contain select-none" />
        </div>
        {n > 1 && <>
          <button type="button" onClick={() => go(-1)} className={`${nav} absolute left-3 top-1/2 -translate-y-1/2 hidden sm:flex`} aria-label="Previous photo" data-testid="viewer-prev"><ChevronLeft className="w-6 h-6" /></button>
          <button type="button" onClick={() => go(1)} className={`${nav} absolute right-3 top-1/2 -translate-y-1/2 hidden sm:flex`} aria-label="Next photo" data-testid="viewer-next"><ChevronRight className="w-6 h-6" /></button>
        </>}
      </div>
      <footer className="px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] space-y-3 max-w-3xl w-full mx-auto">
        <CaptionEditor photo={p} canEdit={editable} onSave={(c) => onSave(p, c)} />
        {p.ai_status === "pending" ? <p className="text-xs text-white/60 flex items-center gap-1.5" data-testid="viewer-ai-note"><Sparkles className="w-3.5 h-3.5" />AI is sorting this photo…</p>
          : p.category_source === "ai" && <p className="text-xs text-white/60 flex items-center gap-1.5" data-testid="viewer-ai-note"><Sparkles className="w-3.5 h-3.5" />{catOf(p, categories) === UNSORTED ? "AI wasn't sure. Pick the right category" : `Sorted by AI${p.ai_confidence ? ` · ${Math.round(p.ai_confidence * 100)}% sure` : ""}`}</p>}
        <div className="flex gap-1.5 overflow-x-auto" data-testid="viewer-category">
          {[...categories, UNSORTED].map((c) => (
            <button key={c} type="button" disabled={!editable} onClick={() => onSave(p, { category: c })} data-testid={`viewer-category-${c.toLowerCase().replace(/\s+/g, "-")}`}
              className={`h-8 px-3 rounded-full text-xs whitespace-nowrap transition-colors ${catOf(p, categories) === c ? "bg-[#fff] text-[#18181b] font-semibold" : "bg-white/10 text-white/80 hover:bg-white/20"} disabled:cursor-default`}>{c}</button>
          ))}
        </div>
        {n > 1 && <div className="flex gap-1.5 overflow-x-auto pb-1" data-testid="viewer-strip">
          {photos.map((q, i) => (
            <button key={q.id} type="button" onClick={() => onIndex(i)} aria-label={`Photo ${i + 1}`}
              className={`shrink-0 w-14 h-14 rounded-md overflow-hidden transition-opacity ${i === index ? "ring-2 ring-[#FF5F15] opacity-100" : "opacity-50 hover:opacity-90"}`}>
              <AuthImage path={q.storage_path} width={320} alt="" className="w-full h-full" />
            </button>
          ))}
        </div>}
      </footer>
    </div>
  );
}
